import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";
import type { Maybe } from "graphql/generated";
import type { Expand, ExpandRecursively } from "types/extensions";
import { breadcrumbSanitize } from "utils/scrubbing";
import { denyUrls } from "./config";
import type { ErrorEvent, SeverityLevel } from "@sentry/react";
import type { BackendTeam } from "graphql/fieldOwners.generated";
import type { GraphQLFormattedError } from "graphql";
import {
  FETCH_FAILURE_MESSAGES,
  isChunkMissingError,
  isChunkReloadReactError,
} from "utils/errors";

// Ad/analytics/telemetry domains whose failed requests surface as benign
// fetch errors. We make no first-party fetches to google.com.
const TRACKING_DOMAINS = [
  "doubleclick.net",
  "googleads.g.doubleclick.net",
  "google-analytics.com",
  "googletagmanager.com",
  "googlesyndication.com",
  "google.com",
  "facebook.com/tr",
  "hs-analytics.net",
  "hubspot.com",
  "statsig.sre.", // statsig.sre.timescale.com / statsig.sre.tigerdata.com
];

// Deliberate-cancel messages only; aborts with other messages still report —
// they may indicate a real problem (e.g. a timeout-based abort).
const ABORT_CANCEL_MESSAGES = [
  "signal is aborted",
  "The user aborted a request",
];

// Shared by the Sentry and PostHog error filters.
export const isKnownBenignException = (
  type: string | undefined,
  value: string | undefined,
): boolean => {
  // DOMExceptions have no `stack`, so the SDK serializes them as string
  // events: `type` stays "Error" and the name moves into the value
  // ("AbortError: The user aborted a request.").
  const abortValue =
    type === "AbortError"
      ? value
      : value?.startsWith("AbortError: ")
        ? value.slice("AbortError: ".length)
        : undefined;
  const isBenignAbortError = ABORT_CANCEL_MESSAGES.some(
    (message) => abortValue?.startsWith(message) === true,
  );

  if (isBenignAbortError) return true;

  // Statsig aborts its slow requests with the string reason "Timeout of Nms
  // expired."; scoping to "expired" keeps Apollo's "Timeout exceeded" reporting.
  if (/Timeout of \d+ms expired/.test(value ?? "")) return true;

  // Matches "Failed to fetch" from ad blockers blocking tracking pixels. Sentry
  // embeds the URL in the message, so we scope to tracking domains — our own API
  // failures still report.
  const isBenignTrackingFetchError =
    type === "TypeError" &&
    value?.startsWith("Failed to fetch") === true &&
    TRACKING_DOMAINS.some((domain) => value.includes(domain));

  if (isBenignTrackingFetchError) return true;

  const errorLike = { name: type ?? "", message: value ?? "" };

  // ChunkLoadError messages produced by a new version being deployed, or
  // React 19's "Should not already be working." thrown by TanStack Router's
  // stale-chunk auto-reload (self-recovers via the reload it's firing).
  return isChunkMissingError(errorLike) || isChunkReloadReactError(errorLike);
};

/**
 * How long after a disturbance a fetch failure is still considered caused by
 * it. The reactive refetches fire within milliseconds of the event, so 5s
 * covers them with margin while staying short enough that a real outage
 * following a tab switch still reports on the next poll cycle (~30s).
 */
const DISTURBANCE_GRACE_MS = 5000;

// 0 = no disturbance seen yet, which keeps every failure outside the window.
let lastDisturbanceAt = 0;

const markDisturbance = () => {
  lastDisturbanceAt = Date.now();
};

/**
 * The fetch error says nothing about why it failed, but the browser events
 * around it do. This tracker timestamps the events that predictably doom
 * in-flight or about-to-fire requests:
 *
 * - pagehide: unloading the page kills every in-flight fetch with
 *   "Failed to fetch" (not AbortError). The dashboard polls constantly, so
 *   nearly every reload or tab close catches a request mid-flight.
 * - online/offline/visibilitychange: connectivity flaps and tab wake-ups
 *   trigger an immediate services refetch (serviceStore's autorun reacts to
 *   commonStore.fetchOptions) before the network is actually usable again,
 *   e.g. wake from sleep or VPN reconnect.
 *
 * `isTransientFetchFailure` then uses proximity to the last such event to
 * decide whether a failure was expected. The UI already copes with these
 * (offline banner, next poll cycle), so reporting them is pure noise — see
 * the Sentry "Failed to fetch" issue families this was added to silence.
 */
export const trackNetworkDisturbances = () => {
  window.addEventListener("pagehide", markDisturbance);
  window.addEventListener("online", markDisturbance);
  window.addEventListener("offline", markDisturbance);
  document.addEventListener("visibilitychange", markDisturbance);
};

/**
 * True when a fetch-failure message is explainable by the environment: the
 * browser is offline, or a disturbance just happened. Failures outside those
 * conditions (user online, tab stable, mid-session) still report — those are
 * the ones that indicate a real problem.
 *
 * Matches on the message so it covers every capture path: the Apollo error
 * link's network branch, store catch blocks wrapping ApolloError, and raw
 * unhandled rejections.
 */
export const isTransientFetchFailure = (value: string | undefined): boolean =>
  FETCH_FAILURE_MESSAGES.some((message) => value?.includes(message)) &&
  (!navigator.onLine || Date.now() - lastDisturbanceAt < DISTURBANCE_GRACE_MS);

// "Timeout exceeded" is the client-side 408 from ApolloLinkTimeout.
const NETWORK_NOISE_MESSAGES = [...FETCH_FAILURE_MESSAGES, "Timeout exceeded"];

/**
 * Network faults rather than product bugs: kept in Sentry but demoted to
 * `level: warning` so the error-level alert rules don't page on them.
 */
export const isNetworkNoiseMessage = (value: string | undefined): boolean =>
  NETWORK_NOISE_MESSAGES.some((message) => value?.includes(message) === true);

/**
 * Owner of a Sentry event, used by team-scoped alert rules (`tags[team]`).
 * API capture sites stamp the owning team from the schema's `@owner`
 * directive ("backend" when no field is attributable); everything left
 * unstamped is frontend-owned by definition.
 */
export type Team = "frontend" | "backend" | BackendTeam;

/** Drops benign/transient events, stamps `team` on the rest, demotes noise. */
export const beforeSend = (event: ErrorEvent): ErrorEvent | null => {
  // Linked errors (`cause` chains) append exception values, so the
  // recognizable one is not always at index 0.
  const values = event.exception?.values ?? [];
  if (values.some((exc) => isKnownBenignException(exc.type, exc.value))) {
    return null;
  }
  if (values.some((exc) => isTransientFetchFailure(exc.value))) {
    return null;
  }

  // A capture site that already set a team wins; an event no site claimed
  // is frontend-owned.
  const tagged: ErrorEvent = event.tags?.team
    ? event
    : { ...event, tags: { ...event.tags, team: "frontend" } };

  // Crashes (mechanism.handled === false) keep their level.
  const isCrash = values.some((exc) => exc.mechanism?.handled === false);
  if (
    tagged.level === "error" &&
    !isCrash &&
    values.some((exc) => isNetworkNoiseMessage(exc.value))
  ) {
    return { ...tagged, level: "warning" };
  }
  return tagged;
};

interface Init {
  dsn: string;
  environment: string;
  release: string;
  // Lets Sentry name transactions by route pattern instead of the raw URL.
  router: AnyRouter;
}

interface User {
  id: string;
  username: string;
  email: string;
}

// Enforces strict types for certain tags, while allowing extra tags as well.
// All tags will get `String()`ified before being sent across the wire anyway.
interface IssueReporterTags {
  [key: string]: any;
  source?: "client" | "network" | "unknown";
  statusCode?: number;
  // Routes team-scoped Sentry alerts; defaults to "frontend" in `beforeSend`.
  team?: Team;
  serviceId?: string;
  projectId?: Maybe<string>;
  error?: unknown;
  componentStack?: string | null;
  errors?: readonly GraphQLFormattedError[];
  // Overrides Sentry's default stack-based grouping. Not sent as a tag.
  fingerprint?: string[];
  // Reports the event as unhandled (mechanism.handled=false) instead of the
  // captureException default of handled. Not sent as a tag. Used for route
  // crashes so they read as `error.unhandled:true`. See `send`.
  handled?: boolean;
}

interface IssueReporterFn {
  (message: string, tags?: IssueReporterTags): void;
}

// Similar interface to JS `console` and
// Log4j(s) https://github.com/log4js-node/log4js-node
interface IssueReporterInterface {
  init: (config: Init) => void;
  setUser: (user: Expand<User> | null) => void;
  debug: ExpandRecursively<IssueReporterFn>;
  info: ExpandRecursively<IssueReporterFn>;
  log: ExpandRecursively<IssueReporterFn>;
  warning: ExpandRecursively<IssueReporterFn>;
  error: ExpandRecursively<IssueReporterFn>;
}

const send = (
  level: SeverityLevel,
  message: string,
  tags: IssueReporterTags,
): void => {
  const { error, componentStack, errors, fingerprint, handled, ...restTags } =
    tags;
  // A string capture makes the SDK flag the event `mechanism.synthetic`, which
  // tells Sentry to ignore the exception type and title the issue after the
  // crash frame's function name instead — minified, so `JI`/`xu`. Wrapping the
  // message in a real Error keeps the type and titles the issue by its message.
  const exception = error instanceof Error ? error : new Error(message);

  const captureContext = {
    tags: { ...restTags, message },
    level,
    ...(fingerprint ? { fingerprint } : {}),
    extra: {
      ...(componentStack ? { componentStack } : {}),
      ...(error && !(error instanceof Error) ? { errorValue: error } : {}),
      ...(errors?.length ? { errors: JSON.stringify(errors) } : {}),
    },
  };

  // Errors caught by an error boundary (route crashes) are reported manually,
  // so the SDK marks them handled by default. Flagging them unhandled makes
  // them read as `error.unhandled:true` — how the route-crash Sentry alert
  // selects real crashes — and counts them against the crash-free session rate.
  if (handled === false) {
    Sentry.captureException(exception, {
      captureContext,
      mechanism: { handled: false, type: "generic" },
    });
    return;
  }

  Sentry.captureException(exception, captureContext);
};

// Sets the global scope with user data when we have it.
const userScope = (user: User | null) => {
  Sentry.getCurrentScope().setUser({ ...user });
};

export const IssueReporter: IssueReporterInterface = {
  init: ({ dsn, environment, release, router }) => {
    trackNetworkDisturbances();

    Sentry.init({
      dsn,
      environment,
      release,
      denyUrls: denyUrls,
      attachStacktrace: true,
      beforeBreadcrumb: breadcrumbSanitize,
      beforeSend,
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.graphqlClientIntegration({
          endpoints: [/\/api\/query$/],
        }),
        // Tracks crash-free session rate per release (release-health alerts).
        Sentry.browserSessionIntegration(),
        // console.warn/error as Sentry Logs (not issues); relies on enableLogs.
        Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
      ],
      tracesSampleRate: 0.01,
      enableLogs: true,
    });
  },
  setUser: (user) => userScope(user),
  debug: (message, tags = {}) => send("debug", message, tags),
  info: (message, tags = {}) => send("info", message, tags),
  log: (message, tags = {}) => send("log", message, tags),
  warning: (message, tags = {}) => send("warning", message, tags),
  error: (message, tags = {}) => send("error", message, tags),
};
